import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { firstValueFrom } from 'rxjs';
import { LoadingSpinnerService } from '../../core/data/loading-spinner.service';
import { ICurrentBoard } from '../../core/models/current-board.model';
import { BoardService } from '../../shared/services/board.service';
import { environment } from '../../../environments/environment';

/**
 * Promotes one of the admin's own saved boards to a Pattern Board. Ownership cannot be reassigned
 * in place -- the edit endpoint ignores a changed user_id -- so a copy is created under the shared
 * 'PATTERN_BOARDS' pseudo-user and the admin's original is deleted. The board leaves My Boards and
 * appears in the Pattern Boards tab.
 *
 * Closes with the new board on success, or undefined when dismissed.
 */
@Component({
    selector: 'lynkd-pattern-dialog-board-promote',
    templateUrl: './dialog-board-promote.component.html',
    styleUrls: ['./dialog-board-promote.component.scss']
})
export class DialogBoardPromoteComponent {
    public boardName: string = '';
    public useBrandivioBranding: boolean = environment.useBrandivioBranding;

    public constructor(
        @Inject(MAT_DIALOG_DATA) public data: { boardData: ICurrentBoard },
        public dialogRef: MatDialogRef<DialogBoardPromoteComponent, ICurrentBoard>,
        private readonly _boardService: BoardService,
        private readonly _loadingSpinner: LoadingSpinnerService,
        private readonly _snackBar: MatSnackBar
    ) {
        this.boardName = this.data.boardData.board_name;
    }

    public async promoteBoard(): Promise<void> {
        if (!this.boardName) {
            return;
        }

        this._loadingSpinner.spin$.next(true);

        let promotedBoard: ICurrentBoard;

        // The shared copy is created first: if it fails, the admin's own board is still intact and
        // nothing has been lost. Deleting first would risk destroying the only copy.
        try {
            // The saved board is already stored in its persisted shape -- filter_time,
            // filter_product and the display columns are all pre-formatted -- so the copy carries
            // it across verbatim and only the name is allowed to differ.
            promotedBoard = await firstValueFrom(
                this._boardService.promoteBoardToPatternBoard({
                    ...this.data.boardData,
                    board_name: this.boardName
                })
            );
        } catch (e) {
            this._snackBar.open('Board promotion failed', 'Close', {
                duration: 5000
            });
            this._loadingSpinner.spin$.next(false);

            return;
        }

        try {
            await firstValueFrom(this._boardService.deleteBoard(String(this.data.boardData.board_id)));

            this._snackBar.open(
                `This board has been promoted to a ${this.useBrandivioBranding ? 'Company' : 'Pattern'} board`,
                'Close',
                { duration: 5000 }
            );
        } catch (e) {
            // The shared copy exists, so the promotion half-succeeded. Say so rather than reporting
            // a clean success -- the board is now in both places until the personal one is removed.
            this._snackBar.open(
                `This board was promoted to ${
                    this.useBrandivioBranding ? 'Company' : 'Pattern'
                } boards, but could not be removed from My Boards`,
                'Close',
                { duration: 8000 }
            );
        } finally {
            this._loadingSpinner.spin$.next(false);
        }

        this.dialogRef.close(promotedBoard);
    }
}
